import React, { useEffect, useState } from "react";
import { Form, Input, Button, Radio, Typography, Spin, Card } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";

import DescriptionEditor from "../components/DescriptionEditor";
import { useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import lang from "../helper/lang";
import { noLeadingSpace, noStartSpace } from "../helper/functions";
const { Title } = Typography;

const ContentFormPage = () => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const { id } = useParams(); // from /content/edit/:id
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [contents, setContents] = useState("");
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };
  const passedRecord = location.state?.record;
  // fetch content if editing
  useEffect(() => {
    if (passedRecord) {
      // Use passed record directly (no need to fetch)
      setData(passedRecord);
      form.setFieldsValue({
        name: passedRecord.name,
        description: passedRecord.description,
      });
      setContents(passedRecord.description || "");
      setStatus(passedRecord.status || "draft");
    } else if (id) {
      // No passed data, fetch from backend
      setLoading(true);
      request({
        url: `/admin/content/${id}`,
        method: "GET",
        onSuccess: (res) => {
          setData(res);
          form.setFieldsValue({
            name: res.name,
            description: res.description,
          });
          setContents(res.description || "");
          setStatus(res.status || "draft");
        },
        onError: (err) => {
          ShowToast(
            err?.response?.data?.message || "Failed to fetch content",
            Severty.ERROR
          );
        },
        onFinally: () => setLoading(false),
      });
    } else {
      // New content case
      form.resetFields();
      setContents("");
      setStatus("draft");
    }
  }, [id, passedRecord]);

  const onFinish = (values) => {
    const payload = {
      name: values.name,
      contents,
      status,
    };

    request({
      url: id ? `/admin/content/edit/${id}` : `/admin/content/add`,
      method: id ? "PUT" : "POST",
      data: payload,
      onSuccess: () => {
        ShowToast("Content saved successfully", Severty.SUCCESS);
        navigate("/content-manager");
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || "Failed to save content",
          Severty.ERROR
        );
      },
    });
  };

  return (
    <>
      <div className="back-box-btn">
        <Button onClick={handleBack} className="back-button mb-2">
          <ArrowLeftOutlined /> {lang("Back")}
        </Button>
      </div>
      <Card title={id ? "Edit Content" : "Add New Content"}>
        {loading ? (
          <Spin />
        ) : (
          <Form
            layout="vertical"
            form={form}
            id="add-content-form"
            onFinish={onFinish}
          >
            <Form.Item
              label="Content Title"
              name="name"
              rules={[
                { required: true, message: "Name is required" },
                { validator: noLeadingSpace },
              ]}
            >
              <Input placeholder="e.g. About Us, Terms & Conditions..." />
            </Form.Item>

            <Form.Item
              label="Description (English)"
              name="description"
              rules={[
                { required: true, message: "Description is required" },
                { validator: noStartSpace },
              ]}
              valuePropName="value"
              getValueFromEvent={(content) => content} 
            >
              <DescriptionEditor
                placeholder="Enter content description..."
                value={contents}
                onChange={(val) => setContents(val)}
              />
            </Form.Item>

            <Form.Item label="Status">
              <Radio.Group
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="draft">Save as Draft</Radio.Button>
                <Radio.Button value="published">Publish</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item>
              <Button htmlType="submit" type="primary" block>
                {id ? "Update Content" : "Create Content"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </>
  );
};

export default ContentFormPage;
